import { Heartbeat, ArrowsClockwise, Hammer, CalendarCheck } from "@phosphor-icons/react";
import { gbp } from "./assetUtils";
import { REC_TONE, REC_HEX, REC_LABEL } from "./AssetHealthBadge";

const FACTORS = [["life", "Remaining life", "25%"], ["condition", "Condition", "30%"], ["spend", "Repair spend", "20%"], ["reliability", "Reliability", "15%"], ["downtime", "Uptime", "10%"]];
const bar = (v) => (v >= 70 ? "bg-emerald-500" : v >= 45 ? "bg-amber-500" : "bg-red-500");

function Ring({ score, color }) {
  const r = 34, c = 2 * Math.PI * r, off = c * (1 - Math.max(0, Math.min(100, score)) / 100);
  return (
    <svg viewBox="0 0 84 84" className="h-24 w-24 shrink-0" aria-label={`Health ${score}`}>
      <circle cx="42" cy="42" r={r} fill="none" stroke="currentColor" strokeWidth="7" className="text-muted/60" />
      <circle cx="42" cy="42" r={r} fill="none" stroke={color} strokeWidth="7" strokeLinecap="round" strokeDasharray={c} strokeDashoffset={off} transform="rotate(-90 42 42)" style={{ transition: "stroke-dashoffset .6s ease" }} />
      <text x="42" y="46" textAnchor="middle" fontSize="20" fontWeight="700" fill="currentColor" fontFamily="Chivo, sans-serif">{score}</text>
      <text x="42" y="58" textAnchor="middle" fontSize="7" fill="currentColor" className="text-muted-foreground" opacity="0.7">/ 100</text>
    </svg>
  );
}

/** Replace-vs-repair card. showMoney=false hides repair spend (client portal still sees the replacement estimate). */
export function AssetHealthCard({ health: h, showMoney = true, testid = "asset-health-card" }) {
  if (!h) return null;
  const rec = h.recommendation || "keep";
  return (
    <div className="border border-border rounded-lg p-5 bg-card" data-testid={testid}>
      <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
        <h3 className="font-semibold text-sm flex items-center gap-2"><Heartbeat size={16} weight="bold" /> Asset health · replace or repair?</h3>
        <span className={`inline-flex items-center rounded-md border px-2.5 py-1 text-xs font-semibold ${REC_TONE[rec]}`} data-testid="asset-health-recommendation">{h.recommendation_label || REC_LABEL[rec]}</span>
      </div>
      <div className="grid md:grid-cols-[auto_1fr] gap-5 items-start">
        <div className="flex md:flex-col items-center gap-4 md:gap-2">
          <Ring score={h.score} color={REC_HEX[rec]} />
          <div className="text-xs text-muted-foreground md:text-center">
            {h.replacement_year && <p className="flex items-center gap-1 justify-center"><CalendarCheck size={12} /> Replace {h.replacement_year}</p>}
            {h.replacement_estimate > 0 && <p className="font-medium text-foreground mt-0.5" data-testid="asset-health-estimate">{gbp(h.replacement_estimate)} est.</p>}
          </div>
        </div>
        <div className="space-y-3 min-w-0">
          <div className="space-y-1.5" data-testid="asset-health-factors">
            {FACTORS.map(([k, label, w]) => (
              <div key={k} className="grid grid-cols-[110px_1fr_34px] items-center gap-2 text-xs">
                <span className="text-muted-foreground truncate" title={`Weight ${w}`}>{label}</span>
                <div className="h-2 rounded-full bg-muted overflow-hidden"><div className={`h-full rounded-full ${bar(h.factors?.[k] ?? 0)}`} style={{ width: `${h.factors?.[k] ?? 0}%`, transition: "width .5s ease" }} /></div>
                <span className="text-right font-mono">{h.factors?.[k] ?? 0}</span>
              </div>
            ))}
          </div>
          {h.reasons?.length > 0 && <ul className="text-xs space-y-0.5" data-testid="asset-health-reasons">{h.reasons.map((r, i) => <li key={i} className="flex items-start gap-1.5"><span className="mt-1.5 h-1 w-1 rounded-full bg-foreground/60 shrink-0" />{r}</li>)}</ul>}
          <div className="flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-muted-foreground pt-1 border-t border-border">
            <span className="inline-flex items-center gap-1"><Hammer size={11} /> {h.incidents_12m ?? 0} repair{h.incidents_12m === 1 ? "" : "s"}/faults in 12m</span>
            {h.open_faults > 0 && <span className="text-red-600">{h.open_faults} open fault{h.open_faults === 1 ? "" : "s"}</span>}
            <span className="inline-flex items-center gap-1"><ArrowsClockwise size={11} /> {h.downtime_days_12m ?? 0} days out of service</span>
            {showMoney && h.repair_spend_12m !== undefined && <span>12m repair spend {gbp(h.repair_spend_12m)}{h.spend_ratio !== null && h.spend_ratio !== undefined ? ` (${Math.round(h.spend_ratio * 100)}% of replacement)` : ""}</span>}
          </div>
        </div>
      </div>
    </div>
  );
}
